import * as THREE from 'three'
import { dimLight, DIMMING_DOWN, DIMMING_EAST_WEST, DIMMING_NORTH_SOUTH, DIMMING_UP, Directions } from './chunks'
import { normalToDirection } from './normalToDirection'

const m1 = new THREE.Matrix4()
const n3 = new THREE.Matrix3()
const v1 = new THREE.Vector3()

const vc1 = new THREE.Vector3()
const vc2 = new THREE.Vector3()
const vc3 = new THREE.Vector3()
const vc4 = new THREE.Vector3()

// 0-1 but in the shader its uint8 so we do 0-255
const directionToDimFactor = {
  north: Math.round(0.8 * 255), // DIMMING_NORTH_SOUTH,
  east: Math.round(0.6 * 255), // DIMMING_EAST_WEST,
  south: Math.round(0.8 * 255), // DIMMING_NORTH_SOUTH,
  west: Math.round(0.6 * 255), // DIMMING_EAST_WEST,
  up: Math.round(1 * 255), // DIMMING_UP,
  down: Math.round(0.4 * 255), //DIMMING_DOWN,
}

export function buildNodeFaces(worldMatrix, node, faces /*light, ao, dimming*/) {
  const [sx, sy, sz] = node.size

  // half extents for centered box
  const hx = sx / 2
  const hy = sy / 2
  const hz = sz / 2

  // check which faces to build
  const hasXY = sx > 0 && sy > 0
  const hasYZ = sy > 0 && sz > 0
  const hasXZ = sx > 0 && sz > 0

  const normalMatrix = n3.identity().getNormalMatrix(worldMatrix)

  // helper to add a face
  function addFace(corners, normal, faceData, dirName) {
    const face = {
      nodeId: node.id,
      position: [],
      index: [],
      tileId: null,
      uv: [],
      dimFactor: null,
      wind: [],
      cullable: null,
      full: false,
    }

    // transform normal by world matrix (ignore translation)
    // const worldNormal = v1.fromArray(normal).applyMatrix4(m1.identity().extractRotation(worldMatrix)).normalize()
    const worldNormal = v1.fromArray(normal).applyNormalMatrix(normalMatrix).normalize()

    // derive the best direction this face represents from its normal, for non-axis-aligned boxes this is an approximation
    // but is needed for smooth lighting and face dimming etc
    face.direction = normalToDirection(worldNormal)

    // dimming
    // note: dimming can be precalculated here, but needs to be multiplied with light per draw
    face.dimFactor = directionToDimFactor[face.direction.name]

    // transform corners by world matrix
    for (const corner of corners) {
      const v = corner.applyMatrix4(worldMatrix)
      face.position.push(v.x, v.y, v.z)
    }

    // faces can be oriented however they like but we need to classify which ones are physically bottom-left counter clockwise if looking at
    // it directly, this is used for the light/ao sampler order and AO quad diagonal.
    // face.canonicalOrder is an array that maps actual drawn face vertices to the physical looking-at + start bottom-left + counter clockwise order!
    face.canonicalOrder = getCanonicalOrder(corners, face.direction)

    // faces can be oriented however they like but we need to classify which ones are physically bottom-left counter clockwise if looking at
    // it directly for the light/ao order.
    face.lightSamplers = face.canonicalOrder.map(idx => {
      return baseLightSamplers[face.direction.name][idx]
    })

    // cullable: if all vertices are flush against a direction (neswud) then it is cullable
    // we can only cull/skip drawing faces that are axis aligned and flush on voxel sides
    face.cullable = getCullable(face.direction, face.position)

    // full: if the face is flush against a voxel side and it spans the entire voxel, its "full"
    face.full = face.cullable && getIsFull(face.direction, face.position)

    // indices (two triangles)
    face.index.push(0, 1, 2, 2, 3, 0)

    // tile ids
    face.tileId = faceData.tileId

    // uvs
    if (faceData.uv) {
      for (const [u, v] of faceData.uv) {
        face.uv.push(u, v)
      }
    } else {
      // NOTE: in NodeJS there is no faceData.uv because bindTextureToAsset was never called, we can skip this.
      face.uv.push(0, 0, 1, 0, 1, 1, 0, 1)
    }
    // face.uv.push(0, 0, 1, 0, 1, 1, 0, 1) // correct
    // out.uv.push(0, 1, 1, 1, 1, 0, 0, 0) // incorrec

    if (node.wind === 'anchor') {
      // anchored wind means that vertices y <= 0 have no wind, but vertices y > 0 have wind up to a cap of 255 at the top of the block
      const BLOCK_HEIGHT = 1.0
      for (let i = 0; i < 4; i++) {
        const y = face.position[i * 3 + 1] // get Y component
        // normalize: y=0 -> 0, y=BLOCK_HEIGHT -> 255, clamp above
        const normalized = Math.max(0, Math.min(1, y / BLOCK_HEIGHT))
        face.wind.push(Math.round(normalized * 255))
        // optional: curve it so base is more anchored
        // const curved = normalized * normalized
        // face.wind.push(Math.round(curved * 255))
      }
    } else if (node.wind === 'free') {
      face.wind.push(255, 255, 255, 255)
    } else {
      face.wind.push(0, 0, 0, 0)
    }

    faces.push(face)
  }

  // North face (Z-) - anti-clockwise from bottom-left when facing
  if (hasXY) {
    addFace(
      [vc1.set(hx, -hy, -hz), vc2.set(-hx, -hy, -hz), vc3.set(-hx, hy, -hz), vc4.set(hx, hy, -hz)],
      [0, 0, -1],
      node.faces?.north,
      'north'
    )
  }

  // South face (Z+) - anti-clockwise from bottom-left when facing
  if (hasXY) {
    addFace(
      [vc1.set(-hx, -hy, hz), vc2.set(hx, -hy, hz), vc3.set(hx, hy, hz), vc4.set(-hx, hy, hz)],
      [0, 0, 1],
      node.faces?.south,
      'south'
    )
  }

  // East face (X+) - anti-clockwise from bottom-left when facing
  if (hasYZ) {
    addFace(
      [vc1.set(hx, -hy, hz), vc2.set(hx, -hy, -hz), vc3.set(hx, hy, -hz), vc4.set(hx, hy, hz)],
      [1, 0, 0],
      node.faces?.east,
      'east'
    )
  }

  // West face (X-) - anti-clockwise from bottom-left when facing
  if (hasYZ) {
    addFace(
      [vc1.set(-hx, -hy, -hz), vc2.set(-hx, -hy, hz), vc3.set(-hx, hy, hz), vc4.set(-hx, hy, -hz)],
      [-1, 0, 0],
      node.faces?.west,
      'west'
    )
  }

  // Up face (Y+) - anti-clockwise from bottom-left when facing down at it
  if (hasXZ) {
    addFace(
      [vc1.set(-hx, hy, hz), vc2.set(hx, hy, hz), vc3.set(hx, hy, -hz), vc4.set(-hx, hy, -hz)],
      [0, 1, 0],
      node.faces?.up,
      'up'
    )
  }

  // Down face (Y-) - anti-clockwise from bottom-left when facing up at it
  if (hasXZ) {
    addFace(
      [vc1.set(-hx, -hy, -hz), vc2.set(hx, -hy, -hz), vc3.set(hx, -hy, hz), vc4.set(-hx, -hy, hz)],
      [0, -1, 0],
      node.faces?.down,
      'down'
    )
  }
}

export function buildNodeBasic(worldMatrix, node, out) {
  const [sx, sy, sz] = node.size

  // half extents for centered box
  const hx = sx / 2
  const hy = sy / 2
  const hz = sz / 2

  // check which faces to build
  const hasXY = sx > 0 && sy > 0
  const hasYZ = sy > 0 && sz > 0
  const hasXZ = sx > 0 && sz > 0

  // helper to add a face
  function addFace(corners, normal, faceData) {
    const baseIdx = out.position.length / 3
    // transform corners by world matrix
    for (const corner of corners) {
      const v = v1.fromArray(corner).applyMatrix4(worldMatrix)
      out.position.push(v.x, v.y, v.z)
    }
    // indices (two triangles)
    out.index.push(baseIdx, baseIdx + 1, baseIdx + 2, baseIdx + 2, baseIdx + 3, baseIdx)
  }

  // North face (Z-) - anti-clockwise from bottom-left when facing
  if (hasXY) {
    addFace(
      [
        [hx, -hy, -hz],
        [-hx, -hy, -hz],
        [-hx, hy, -hz],
        [hx, hy, -hz],
      ],
      [0, 0, -1],
      node.faces?.north
    )
  }

  // South face (Z+) - anti-clockwise from bottom-left when facing
  if (hasXY) {
    addFace(
      [
        [-hx, -hy, hz],
        [hx, -hy, hz],
        [hx, hy, hz],
        [-hx, hy, hz],
      ],
      [0, 0, 1],
      node.faces?.south
    )
  }

  // East face (X+) - anti-clockwise from bottom-left when facing
  if (hasYZ) {
    addFace(
      [
        [hx, -hy, hz],
        [hx, -hy, -hz],
        [hx, hy, -hz],
        [hx, hy, hz],
      ],
      [1, 0, 0],
      node.faces?.east
    )
  }

  // West face (X-) - anti-clockwise from bottom-left when facing
  if (hasYZ) {
    addFace(
      [
        [-hx, -hy, -hz],
        [-hx, -hy, hz],
        [-hx, hy, hz],
        [-hx, hy, -hz],
      ],
      [-1, 0, 0],
      node.faces?.west
    )
  }

  // Up face (Y+) - anti-clockwise from bottom-left when facing down at it
  if (hasXZ) {
    addFace(
      [
        [-hx, hy, hz],
        [hx, hy, hz],
        [hx, hy, -hz],
        [-hx, hy, -hz],
      ],
      [0, 1, 0],
      node.faces?.up
    )
  }

  // Down face (Y-) - anti-clockwise from bottom-left when facing up at it
  if (hasXZ) {
    addFace(
      [
        [-hx, -hy, -hz],
        [hx, -hy, -hz],
        [hx, -hy, hz],
        [-hx, -hy, hz],
      ],
      [0, -1, 0],
      node.faces?.down
    )
  }
}

function approx(a, b, eps = 1e-5) {
  return Math.abs(a - b) <= eps
}

function checkCullablePositions(positions, axisIdx, shouldBe) {
  // prettier-ignore
  const values = [
    positions[axisIdx],
    positions[axisIdx + 3],
    positions[axisIdx + 6],
    positions[axisIdx + 9],
  ]
  return values.every(n => approx(n, shouldBe))
}

const cullableChecks = {
  north: positions => checkCullablePositions(positions, 2, -0.5), // all z -0.5 means flush with north
  east: positions => checkCullablePositions(positions, 0, 0.5), // all x 0.5 means flush with east
  south: positions => checkCullablePositions(positions, 2, 0.5), // all z 0.5 means flush with south
  west: positions => checkCullablePositions(positions, 0, -0.5), // all x 0.5 means flush with west
  up: positions => checkCullablePositions(positions, 1, 1), // all y 1 means flush with up
  down: positions => checkCullablePositions(positions, 1, 0), // all y 0 means flush with down
}
function getCullable(dir, positions) {
  return cullableChecks[dir.name](positions)
}

function getIsFull(dir, positions) {
  // positions: [x0,y0,z0, x1,y1,z1, x2,y2,z2, x3,y3,z3]

  function minMax(axisIdx) {
    // axisIdx: 0 => x, 1 => y, 2 => z
    const v0 = positions[axisIdx]
    const v1 = positions[axisIdx + 3]
    const v2 = positions[axisIdx + 6]
    const v3 = positions[axisIdx + 9]
    const min = Math.min(v0, v1, v2, v3)
    const max = Math.max(v0, v1, v2, v3)
    return [min, max]
  }

  if (dir.axis === 'z') {
    // plane z = -0.5 (north) or z = 0.5 (south) already checked by getCullableSide
    const [minX, maxX] = minMax(0)
    const [minY, maxY] = minMax(1)

    // full face: x in [-0.5, 0.5], y in [0, 1]
    return approx(minX, -0.5) && approx(maxX, 0.5) && approx(minY, 0.0) && approx(maxY, 1.0)
  }

  if (dir.axis === 'x') {
    // plane x = 0.5 (east) or x = -0.5 (west) already checked
    const [minZ, maxZ] = minMax(2)
    const [minY, maxY] = minMax(1)

    // full face: z in [-0.5, 0.5], y in [0, 1]
    return approx(minZ, -0.5) && approx(maxZ, 0.5) && approx(minY, 0.0) && approx(maxY, 1.0)
  }

  if (dir.axis === 'y') {
    // plane y = 1 (up) or y = 0 (down) already checked
    const [minX, maxX] = minMax(0)
    const [minZ, maxZ] = minMax(2)

    // full face: x in [-0.5, 0.5], z in [-0.5, 0.5]
    return approx(minX, -0.5) && approx(maxX, 0.5) && approx(minZ, -0.5) && approx(maxZ, 0.5)
  }

  return false
}

// prettier-ignore
const baseLightSamplers = {
  north: [
    // back story: all vert positions are when facing the face, counter clockwise starting from bottom-left
    // for each vertex, these below are the offsets to find: base, side1, side2, corner (for smooth lighting and ao sampling)
    // the fourth value is the direction back to the origin to test whether that direction is opaque
    // note that for corners there are 2 possible directions but we just pick one because theoretically theyd be the same
    // also note base direction is not really useful since we know its not opaque if we are drawing a face there haha
    [[0, 0, -1, 'south'], [1, 0, -1, 'west'], [0, -1, -1, 'up'], [1, -1, -1, 'up']],
    [[0, 0, -1, 'south'], [0, -1, -1, 'up'], [-1, 0, -1, 'east'], [-1, -1, -1, 'up']],
    [[0, 0, -1, 'south'], [-1, 0, -1, 'east'], [0, 1, -1, 'down'], [-1, 1, -1, 'down']],
    [[0, 0, -1, 'south'], [0, 1, -1, 'down'], [1, 0, -1, 'west'], [1, 1, -1, 'down']],
  ],
  east: [
    [[1, 0, 0, 'west'], [1, 0, 1, 'north'], [1, -1, 0, 'up'], [1, -1, 1, 'up']],
    [[1, 0, 0, 'west'], [1, 0, -1, 'south'], [1, -1, 0, 'up'], [1, -1, -1, 'up']],
    [[1, 0, 0, 'west'], [1, 1, 0, 'down'], [1, 0, -1, 'south'], [1, 1, -1, 'down']],
    [[1, 0, 0, 'west'], [1, 1, 0, 'down'], [1, 0, 1, 'north'], [1, 1, 1, 'down']],
  ],
  south: [
    [[0, 0, 1, 'north'], [-1, 0, 1, 'east'], [0, -1, 1, 'up'], [-1, -1, 1, 'up']],
    [[0, 0, 1, 'north'], [0, -1, 1, 'up'], [1, 0, 1, 'west'], [1, -1, 1, 'up']],
    [[0, 0, 1, 'north'], [1, 0, 1, 'west'], [0, 1, 1, 'down'], [1, 1, 1, 'down']],
    [[0, 0, 1, 'north'], [0, 1, 1, 'down'], [-1, 0, 1, 'east'], [-1, 1, 1, 'down']],
  ],
  west: [
    [[-1, 0, 0, 'east'], [-1, 0, -1, 'south'], [-1, -1, 0, 'up'], [-1, -1, -1, 'up']],
    [[-1, 0, 0, 'east'], [-1, 0, 1, 'north'], [-1, -1, 0, 'up'], [-1, -1, 1, 'up']],
    [[-1, 0, 0, 'east'], [-1, 1, 0, 'down'], [-1, 0, 1, 'north'], [-1, 1, 1, 'down']],
    [[-1, 0, 0, 'east'], [-1, 1, 0, 'down'], [-1, 0, -1, 'south'], [-1, 1, -1, 'down']],
  ],
  up: [
    [[0, 1, 0, 'down'], [0, 1, 1, 'north'], [-1, 1, 0, 'east'], [-1, 1, 1, 'north']], 
    [[0, 1, 0, 'down'], [1, 1, 0, 'west'], [0, 1, 1, 'north'], [1, 1, 1, 'north']], 
    [[0, 1, 0, 'down'], [0, 1, -1, 'south'], [1, 1, 0, 'west'], [1, 1, -1, 'south']], 
    [[0, 1, 0, 'down'], [-1, 1, 0, 'east'], [0, 1, -1, 'south'], [-1, 1, -1, 'south']], 
  ],
  down: [
    [[0, -1, 0, 'up'], [-1, -1, 0, 'east'], [0, -1, -1, 'south'], [-1, -1, -1, 'south']],
    [[0, -1, 0, 'up'], [0, -1, -1, 'south'], [1, -1, 0, 'west'], [1, -1, -1, 'south']],
    [[0, -1, 0, 'up'], [1, -1, 0, 'west'], [0, -1, 1, 'north'], [1, -1, 1, 'north']],
    [[0, -1, 0, 'up'], [0, -1, 1, 'north'], [-1, -1, 0, 'east'], [-1, -1, 1, 'north']],
  ]
}

const canonicalOrderInfo = {
  north: { verticalAxis: 'y', verticalPositive: true, horizontalAxis: 'x', horizontalPositive: false },
  east: { verticalAxis: 'y', verticalPositive: true, horizontalAxis: 'z', horizontalPositive: false },
  south: { verticalAxis: 'y', verticalPositive: true, horizontalAxis: 'x', horizontalPositive: true },
  west: { verticalAxis: 'y', verticalPositive: true, horizontalAxis: 'z', horizontalPositive: true },
  up: { verticalAxis: 'z', verticalPositive: false, horizontalAxis: 'x', horizontalPositive: true },
  down: { verticalAxis: 'z', verticalPositive: true, horizontalAxis: 'x', horizontalPositive: true },
}

function getCanonicalOrder(corners, direction) {
  const info = canonicalOrderInfo[direction.name]

  // keep original order so we can map back into it.
  const original = [...corners]

  // work on a copy for sorting.
  const verts = [...corners]

  // 1. sort by vertical axis: bottom -> top (in screen space)
  verts.sort((a, b) =>
    info.verticalPositive ? a[info.verticalAxis] - b[info.verticalAxis] : b[info.verticalAxis] - a[info.verticalAxis]
  )

  const bottom = [verts[0], verts[1]]
  const top = [verts[2], verts[3]]

  // 2. sort each row by horizontal axis: left -> right (in screen space)
  const sortHoriz = (a, b) =>
    info.horizontalPositive
      ? a[info.horizontalAxis] - b[info.horizontalAxis] // left = smaller
      : b[info.horizontalAxis] - a[info.horizontalAxis] // left = larger

  bottom.sort(sortHoriz)
  top.sort(sortHoriz)

  // At this point, looking at the face:
  // bottom[0] = bottom-left
  // bottom[1] = bottom-right
  // top[0]    = top-left
  // top[1]    = top-right

  // baseLightSamplers[...] is:
  // [ bottom-left, bottom-right, top-right, top-left ] (CCW from bottom-left)
  const canonicalOrder = [
    bottom[0], // bottom-left
    bottom[1], // bottom-right
    top[1], // top-right
    top[0], // top-left
  ]

  // 3. build new sampler array that matches the *original* corner order
  const canonicalOrderIndexes = original.map(corner => {
    const idx = canonicalOrder.indexOf(corner)
    return idx
  })

  return canonicalOrderIndexes
}
